import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Star, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PlanPurchaseModal from './PlanPurchaseModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlansCatalog = ({ member, onPurchaseSuccess }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      // Sort by display_order and filter active plans
      const sortedPlans = response.data
        .filter(plan => plan.is_active !== false)
        .sort((a, b) => (a.display_order || 999) - (b.display_order || 999));
      setPlans(sortedPlans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load membership plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setPurchaseModalOpen(true);
  };

  const handlePurchaseSuccess = (data) => {
    toast.success('Membership activated successfully!');
    if (onPurchaseSuccess) {
      onPurchaseSuccess(data);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No membership plans available at this time</p>
        </CardContent>
      </Card>
    );
  }

  // Check if user already has an active membership
  const hasActiveMembership = member && member.membership_status === 'active';

  return (
    <div className="space-y-6">
      {hasActiveMembership && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-blue-800">
                You already have an active membership. Use the renewal option to extend your membership.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = member && member.membership_plan_id === plan.id;
          
          return (
            <Card
              key={plan.id}
              className={`relative transition-all hover:shadow-lg ${
                plan.is_most_popular
                  ? 'border-2 border-orange-400 shadow-md'
                  : 'border border-gray-200'
              } ${isCurrentPlan ? 'ring-2 ring-teal-500' : ''}`}
            >
              {/* Most Popular Badge */}
              {plan.is_most_popular && (
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                  <Badge className="bg-orange-500 flex items-center gap-1 px-3 py-1">
                    <Star className="h-3 w-3 fill-current" />
                    MOST POPULAR
                  </Badge>
                </div>
              )}

              {/* Current Plan Badge */}
              {isCurrentPlan && (
                <div className="absolute top-0 left-0 transform -translate-x-2 -translate-y-2">
                  <Badge className="bg-teal-600 flex items-center gap-1 px-3 py-1">
                    <CheckCircle className="h-3 w-3" />
                    YOUR PLAN
                  </Badge>
                </div>
              )}

              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription className="text-sm">
                  {plan.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Price */}
                <div className="py-4 border-y border-gray-100">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900">
                      ${plan.price?.toFixed(0)}
                    </span>
                    <span className="text-gray-500">
                      / {plan.duration_months} {plan.duration_months === 1 ? 'month' : 'months'}
                    </span>
                  </div>
                </div>

                {/* Benefits */}
                {plan.benefits && plan.benefits.length > 0 && (
                  <div className="space-y-2">
                    {plan.benefits.slice(0, 5).map((benefit, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0 mt-1" />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                    {plan.benefits.length > 5 && (
                      <p className="text-sm text-gray-500 pl-6">
                        +{plan.benefits.length - 5} more benefits
                      </p>
                    )}
                  </div>
                )}
              </CardContent>

              <CardFooter>
                <Button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={hasActiveMembership || isCurrentPlan}
                  className={`w-full ${
                    plan.is_most_popular
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                      : 'bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700'
                  }`}
                >
                  {isCurrentPlan
                    ? 'Current Plan'
                    : hasActiveMembership
                    ? 'Already Subscribed'
                    : 'Select Plan'}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Purchase Modal */}
      <PlanPurchaseModal
        open={purchaseModalOpen}
        onOpenChange={setPurchaseModalOpen}
        plan={selectedPlan}
        onSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

export default PlansCatalog;
